import { Action } from '@reduxjs/toolkit';
import isObject from 'lodash/isObject';

import { DeepPartial } from 'types/util';

import { SrpFilterName, SrpFiltersState, SrpFilterValue, SrpFilterValues } from 'types/srpFilters';

export enum SrpActionType {
  SetFilters = 'srpFilters/setFilters',
}

export interface SetSrpFiltersAction {
  type: SrpActionType.SetFilters;
  payload: DeepPartial<SrpFilterValues>;
}

export const setSrpFilters = (filters: DeepPartial<SrpFilterValues>): SetSrpFiltersAction => {
  return {
    type: SrpActionType.SetFilters,
    payload: filters,
  };
};

export const srpFilterInitialValues: SrpFilterValues = {
  [SrpFilterName.Year]: {
    min: 1940,
    max: new Date().getFullYear() + 1,
  },
  [SrpFilterName.Condition]: {
    new: false,
    'certified pre-owned': false,
    used: false,
  },
  [SrpFilterName.Cylinders]: {
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
    8: false,
    10: false,
    12: false,
  },
  [SrpFilterName.MakeModel]: {},
  [SrpFilterName.Radius]: { number: 50 },
  [SrpFilterName.SortBy]: undefined,
  [SrpFilterName.Price]: {
    hideNoPriceListings: false,
  },
};

export const srpFiltersInitialState: SrpFiltersState = {
  initialized: false,
  values: srpFilterInitialValues,
};

export const srpFiltersReducer = (
  state: SrpFiltersState = srpFiltersInitialState,
  action: Action
): SrpFiltersState => {
  if (!Object.values(SrpActionType).includes(action.type)) {
    return state;
  }

  if (action.type === SrpActionType.SetFilters) {
    const setFiltersAction = action as SetSrpFiltersAction;
    const values = mergeDeep<SrpFilterValues>({ ...state.values }, setFiltersAction.payload);
    return {
      initialized: true,
      values,
    };
  }

  return state;
};

function mergeDeep<T>(...objects: DeepPartial<T>[]): T {
  return objects.reduce((prev: any, obj: any) => {
    Object.keys(obj).forEach((key) => {
      /* eslint-disable no-param-reassign */
      const pVal = prev[key];
      const oVal = obj[key];

      if (Array.isArray(pVal) && Array.isArray(oVal)) {
        prev[key] = oVal;
      } else if (isObject(pVal) && isObject(oVal)) {
        prev[key] = mergeDeep(pVal, oVal);
      } else {
        prev[key] = oVal;
      }
    });

    return prev;
  }, {});
}

export const getSrpFiltersInitialized = (state: SrpFiltersState): boolean => {
  return state.initialized;
};

export const getSrpFilterValues = (state: SrpFiltersState): SrpFilterValues => {
  return state.values;
};

export const getSrpFilterValue = <T extends SrpFilterValue>(
  state: SrpFiltersState,
  name: SrpFilterName
): T => {
  return state.values[name] as T;
};
